import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { forwardRef, useEffect, useCallback } from 'react';
// material
import { Box } from '@mui/material';
// utils
import track from '../utils/analytics';

// ----------------------------------------------------------------------

// Fallback when a page passes no title, matching public/index.html so a page
// that forgets one looks intentional rather than blank.
const DEFAULT_TITLE = 'ResiView';

// The title is set directly rather than through react-helmet-async (card 223).
//
// Helmet was silently emitting NOTHING in this app — no `[data-rh]` nodes in
// the document at all, in dev and in the deployed build alike — so every route
// showed index.html's default title, and the <Helmet>-rendered Google Analytics
// tag never made it into the page either. It is not a StrictMode interaction;
// removing StrictMode changes nothing.
//
// Two lines of `document.title` are deterministic, need no provider, and cannot
// fail quietly. There is nothing a tag manager gives this app that is worth
// having page titles depend on a dependency that does not work.

const Page = forwardRef(({ children, title = '', ...other }, ref) => {
  const { pathname } = useLocation();

  const sendPageViewEvent = useCallback(() => {
    track.pageview({
      page_path: pathname
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    sendPageViewEvent();
  }, [sendPageViewEvent]);

  useEffect(() => {
    document.title = title || DEFAULT_TITLE;
  }, [title]);

  return (
    <Box ref={ref} {...other}>
      {children}
    </Box>
  );
});

Page.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string
};

export default Page;
